import { captureException, SeverityLevel, withScope } from '@sentry/react';

import { Logger } from './Logger';

export function sentryLog(
  message: unknown,
  level: SeverityLevel = 'error',
  context: Record<string, unknown> = {},
): void {
  withScope((scope) => {
    scope.setLevel(level);
    scope.setExtras(context);
    captureException(message);
  });
}

function getContext(message: unknown): Record<string, unknown> | undefined {
  if (typeof message === 'object' && message !== null && 'context' in message) {
    const { context } = message;
    if (
      typeof context === 'object' &&
      !Array.isArray(context) &&
      context !== null
    ) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      return context as Record<string, unknown>;
    }
  }

  return undefined;
}

export function createSentryLogger(): Logger {
  function logWithContext(
    logLevel: SeverityLevel,
    firstMessage: unknown,
    messages: unknown[],
  ): void {
    const context = getContext(firstMessage);
    if (context) {
      if (messages.length === 1) {
        sentryLog(messages[0], logLevel, context);
      } else {
        sentryLog(messages, logLevel, context);
      }
    } else if (messages.length === 0) {
      sentryLog(firstMessage, logLevel);
    } else {
      sentryLog([firstMessage, ...messages], logLevel);
    }
  }

  function log(firstMessage: unknown, ...messages: unknown[]): void {
    logWithContext('log', firstMessage, messages);
  }

  function info(firstMessage: unknown, ...messages: unknown[]): void {
    logWithContext('info', firstMessage, messages);
  }

  function warn(firstMessage: unknown, ...messages: unknown[]): void {
    logWithContext('warning', firstMessage, messages);
  }

  function error(firstMessage: unknown, ...messages: unknown[]): void {
    logWithContext('error', firstMessage, messages);
  }

  return { log, info, warn, error };
}
